import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import RecordForm from "@/components/RecordForm";
import ImportDialog from "@/components/ImportDialog";
import BulkEditDialog from "@/components/BulkEditDialog";
import DatePicker from "@/components/DatePicker";
import { api } from "@/lib/api";
import { fmt, fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import ConfirmDialog from "@/components/ConfirmDialog";
import { Plus, Pencil, Trash2, FileSpreadsheet, FileText, Search, X, Upload, Layers, Gavel, RotateCcw, FileCode } from "lucide-react";
import ColumnFilter from "@/components/ColumnFilter";

const COLUMNS = [
  { key: "tarih", label: "Tarih", type: "date" },
  { key: "vade_tarihi", label: "Vade", type: "date" },
  { key: "musteri", label: "Müşteri", type: "text" },
  { key: "urun", label: "Ürün", type: "text" },
  { key: "dosya_no", label: "Dosya No", type: "text" },
  { key: "icra_dairesi", label: "İcra Dairesi", type: "text" },
  { key: "il", label: "İl", type: "text" },
  { key: "alis_fiyati", label: "Alış F.", type: "money" },
  { key: "satis_fiyati", label: "Satış F.", type: "money" },
  { key: "icra_tutari", label: "İcra Tutarı", type: "money" },
  { key: "tahsilat", label: "Tahsilat", type: "money", color: "success" },
  { key: "faiz_tahsilat", label: "Faiz Tahsilatı", type: "money", color: "success" },
  { key: "odenen", label: "Ödenen", type: "money" },
  { key: "asil_alacak", label: "Asıl Alacak", type: "money" },
  { key: "kalan", label: "Kalan", type: "money" },
  { key: "brut_risk", label: "Brüt Risk", type: "money", color: "danger" },
  { key: "net_risk", label: "Net Risk", type: "money", color: "danger" },
  { key: "kar", label: "Kâr", type: "money", color: "success" },
];

export default function Records() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [musteri, setMusteri] = useState("");
  const [durumF, setDurumF] = useState("");
  const [colSel, setColSel] = useState({});
  const [menuCol, setMenuCol] = useState(null);
  const [sort, setSort] = useState({ key: null, dir: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (start) params.start = start;
      if (end) params.end = end;
      if (musteri) params.musteri = musteri;
      if (durumF) params.durum = durumF;
      const { data } = await api.get("/records", { params });
      setRecords(data);
      setSelected(new Set());
    } catch (e) {
      toast.error("Kayıtlar yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [start, end, musteri, durumF]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSubmit = async (payload) => {
    try {
      if (editing) {
        await api.put(`/records/${editing.id}`, payload);
        toast.success("Kayıt güncellendi");
      } else {
        await api.post("/records", payload);
        toast.success("Kayıt eklendi");
      }
      setShowForm(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Kayıt kaydedilemedi");
    }
  };

  const handleDelete = (id) => {
    setConfirm({
      title: "Kaydı Sil",
      description: "Bu icra kaydını silmek istediğinize emin misiniz? Bu işlem geri alınamaz.",
      onConfirm: async () => {
        try {
          await api.delete(`/records/${id}`);
          toast.success("Kayıt silindi");
          load();
        } catch (e) {
          toast.error(e.response?.data?.detail || "Kayıt silinemedi");
        } finally {
          setConfirm(null);
        }
      },
    });
  };

  const toggleOne = (id) =>
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const toggleAll = () =>
    setSelected((prev) => (prev.size === records.length ? new Set() : new Set(records.map((r) => r.id))));

  const handleBulkDelete = () => {
    setConfirm({
      title: "Seçilenleri Sil",
      description: `Seçili ${selected.size} kaydı silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`,
      onConfirm: async () => {
        try {
          const { data } = await api.post("/records/bulk-delete", { ids: [...selected] });
          toast.success(`${data.deleted} kayıt silindi`);
          load();
        } catch (e) {
          toast.error(e.response?.data?.detail || "Toplu silme başarısız");
        } finally {
          setConfirm(null);
        }
      },
    });
  };

  const handleBulkUpdate = async (field, value) => {
    try {
      const { data } = await api.post("/records/bulk-update", { ids: [...selected], updates: { [field]: value } });
      toast.success(`${data.modified} kayıt güncellendi`);
      setShowBulkEdit(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Toplu güncelleme başarısız");
    }
  };


  const download = async (type) => {
    try {
      const params = {};
      if (start) params.start = start;
      if (end) params.end = end;
      if (musteri) params.musteri = musteri;
      const res = await api.get(`/records/export/${type}`, { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = type === "excel" ? "nakit_akis.xlsx" : "nakit_akis.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${type === "excel" ? "Excel" : "PDF"} indirildi`);
    } catch (e) {
      toast.error("Dışa aktarma başarısız");
    }
  };

  const handleTopluXml = async () => {
    if (selected.size === 0) return;
    const tid = toast.loading(`${selected.size} föy için XML hazırlanıyor…`);
    try {
      const res = await api.post("/records/takip-xml-zip", { ids: [...selected], ornek: "10" }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Toplu_Takip_Ornek10_${selected.size}dosya.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Toplu XML (zip) indirildi", { id: tid });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Toplu XML başarısız", { id: tid });
    }
  };
  const clearFilters = () => {
    setStart("");
    setEnd("");
    setMusteri("");
    setDurumF("");
    setColSel({});
  };

  const undoIcra = async (r) => {
    if (!window.confirm(`${r.musteri} dosyası Cari'ye geri döndürülecek. İcra kaydı silinip vadeler Cari hesaplarına yeniden yüklenecek. Onaylıyor musunuz?`)) return;
    try {
      const { data } = await api.post(`/records/${r.id}/undo-icra`);
      toast.success(`${data.restored} vade Cari'ye geri alındı`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Geri alınamadı");
    }
  };

  const toggleInfaz = async (r) => {
    try {
      const yeni = r.durum === "İNFAZ" ? null : "İNFAZ";
      await api.put(`/records/${r.id}/durum`, { durum: yeni });
      toast.success(yeni ? "Dosya infaz edildi" : "İnfaz geri alındı");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "İşlem başarısız");
    }
  };

  const cellColor = (col, val) => {
    if (col.color === "danger" && val > 0) return "text-[#A63A3A]";
    if (col.color === "success" && val > 0) return "text-[#2F6E4E]";
    if (col.color === "interest" && val > 0) return "text-[#7C5E9B]";
    return "text-[#26231E]";
  };

  const trLower = (s) => String(s ?? "").toLocaleLowerCase("tr");
  const disp = (c, r) => c.type === "money" ? fmt(r[c.key]) : c.type === "date" ? fmtDate(r[c.key]) : String(r[c.key] ?? "");
  const distinct = (c) => {
    const arr = [...new Set(records.map((r) => disp(c, r)))];
    if (c.type === "money") arr.sort((a, b) => (parseFloat(String(a).replace(/[^\d,-]/g, "").replace(",", ".")) || 0) - (parseFloat(String(b).replace(/[^\d,-]/g, "").replace(",", ".")) || 0));
    else arr.sort((a, b) => trLower(a).localeCompare(trLower(b), "tr"));
    return arr;
  };
  const filtered = records.filter((r) => COLUMNS.every((c) => { const sel = colSel[c.key]; return !sel || sel.includes(disp(c, r)); }));
  const totals = {};
  COLUMNS.forEach((c) => { if (c.type === "money") totals[c.key] = filtered.reduce((s, r) => s + (Number(r[c.key]) || 0), 0); });
  let view = filtered;
  if (sort.key) {
    const col = COLUMNS.find((c) => c.key === sort.key);
    view = [...filtered].sort((a, b) => {
      let av = a[sort.key], bv = b[sort.key];
      if (col?.type === "money") { av = Number(av) || 0; bv = Number(bv) || 0; }
      else if (col?.type === "date") { av = av || ""; bv = bv || ""; }
      else { av = trLower(av); bv = trLower(bv); }
      if (av < bv) return sort.dir === "asc" ? -1 : 1;
      if (av > bv) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
  }

  return (
    <Layout>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="kicker mb-1">İCRA KAYITLARI</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">İcralık Cari Hesaplar</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowImport(true)}
            data-testid="import-excel-button"
            className="flex items-center gap-1.5 px-3 py-2 border border-[#E7DECC] bg-white rounded-sm text-sm font-medium hover:bg-[#EFE7D6] transition-colors duration-150"
          >
            <Upload className="w-4 h-4 text-[#B8933F]" /> Excel ile Ekle
          </button>
          <button
            onClick={() => download("excel")}
            data-testid="export-excel-button"
            className="flex items-center gap-1.5 px-3 py-2 border border-[#E7DECC] bg-white rounded-sm text-sm font-medium hover:bg-[#EFE7D6] transition-colors duration-150"
          >
            <FileSpreadsheet className="w-4 h-4 text-[#2F6E4E]" /> Excel
          </button>
          <button
            onClick={() => download("pdf")}
            data-testid="export-pdf-button"
            className="flex items-center gap-1.5 px-3 py-2 border border-[#E7DECC] bg-white rounded-sm text-sm font-medium hover:bg-[#EFE7D6] transition-colors duration-150"
          >
            <FileText className="w-4 h-4 text-[#A63A3A]" /> PDF
          </button>
          <button
            onClick={() => {
              setEditing(null);
              setShowForm(true);
            }}
            data-testid="add-record-button"
            className="flex items-center gap-1.5 px-4 py-2 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-sm font-medium transition-colors duration-150"
          >
            <Plus className="w-4 h-4" /> Yeni Kayıt
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 mb-5 p-4 bg-white border border-[#E7DECC] rounded-sm">
        <div className="w-[180px]">
          <label className="kicker block mb-1.5">BAŞLANGIÇ</label>
          <DatePicker value={start} onChange={setStart} placeholder="Başlangıç" testId="filter-start-input" />
        </div>
        <div className="w-[180px]">
          <label className="kicker block mb-1.5">BİTİŞ</label>
          <DatePicker value={end} onChange={setEnd} placeholder="Bitiş" testId="filter-end-input" />
        </div>
        <div className="flex-1 min-w-[180px]">
          <label className="kicker block mb-1.5">MÜŞTERİ ARA</label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8C8377]" />
            <input
              value={musteri}
              onChange={(e) => setMusteri(e.target.value)}
              data-testid="filter-musteri-input"
              placeholder="Müşteri adı..."
              className="w-full pl-9 pr-3 py-2 border border-[#E7DECC] rounded-sm focus:outline-none focus:border-[#B8933F]"
            />
          </div>
        </div>
        <div className="w-[170px]">
          <label className="kicker block mb-1.5">DURUM</label>
          <select
            value={durumF}
            onChange={(e) => setDurumF(e.target.value)}
            data-testid="filter-durum"
            className="w-full px-3 py-2 border border-[#E7DECC] bg-white rounded-sm focus:outline-none focus:border-[#B8933F]"
          >
            <option value="">Tümü</option>
            <option value="İNFAZ">Sadece İnfaz</option>
            <option value="Devam">Sadece Devam</option>
          </select>
        </div>
        {(start || end || musteri || durumF) && (
          <button
            onClick={clearFilters}
            data-testid="clear-filters-button"
            className="flex items-center gap-1 px-3 py-2 text-sm text-[#8C8377] hover:text-[#A63A3A] transition-colors duration-150"
          >
            <X className="w-4 h-4" /> Temizle
          </button>
        )}
      </div>

      {/* Selection toolbar */}
      {selected.size > 0 && (
        <div
          className="flex flex-wrap items-center justify-between gap-3 mb-4 px-4 py-3 bg-[#B8933F] text-white rounded-sm"
          data-testid="bulk-toolbar"
        >
          <span className="text-sm font-medium" data-testid="bulk-selected-count">
            {selected.size} kayıt seçildi
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={handleTopluXml}
              data-testid="bulk-xml-button"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white/15 hover:bg-white/25 rounded-sm text-sm font-medium transition-colors duration-150"
            >
              <FileCode className="w-4 h-4" /> Toplu XML İndir (Örnek 10)
            </button>
            {isAdmin && (
              <button
                onClick={() => setShowBulkEdit(true)}
                data-testid="bulk-edit-button"
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white/15 hover:bg-white/25 rounded-sm text-sm font-medium transition-colors duration-150"
              >
                <Layers className="w-4 h-4" /> Toplu Düzenle
              </button>
            )}
            {isAdmin && (
              <button
                onClick={handleBulkDelete}
                data-testid="bulk-delete-button"
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[#A63A3A] hover:bg-[#e02d23] rounded-sm text-sm font-medium transition-colors duration-150"
              >
                <Trash2 className="w-4 h-4" /> Seçilenleri Sil
              </button>
            )}
            <button
              onClick={() => setSelected(new Set())}
              data-testid="bulk-clear-button"
              className="flex items-center gap-1 px-2 py-1.5 hover:bg-white/15 rounded-sm text-sm transition-colors duration-150"
            >
              <X className="w-4 h-4" /> Temizle
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-[#E7DECC] rounded-sm overflow-auto max-h-[calc(100vh-16rem)]" data-testid="records-table">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-[#F3ECDD]">
            <tr className="border-b border-[#E7DECC] bg-[#F3ECDD]">
              <th className="px-3 py-2.5 w-10">
                <input
                  type="checkbox"
                  checked={records.length > 0 && selected.size === records.length}
                  onChange={toggleAll}
                  data-testid="select-all-records"
                  className="w-4 h-4 accent-[#B8933F] cursor-pointer align-middle"
                />
              </th>
              {COLUMNS.map((c) => (
                <th key={c.key} className={`px-3 py-2.5 kicker whitespace-nowrap ${c.type === "money" ? "text-right" : "text-left"}`}>
                  <div className={`flex items-center gap-1 ${c.type === "money" ? "justify-end" : "justify-between"}`}>
                    <span>{c.label}{sort.key === c.key ? (sort.dir === "asc" ? " ↑" : " ↓") : ""}</span>
                    <ColumnFilter
                      col={c}
                      values={distinct(c)}
                      selected={colSel[c.key] ?? null}
                      onChange={(v) => setColSel((p) => ({ ...p, [c.key]: v }))}
                      sortDir={sort.key === c.key ? sort.dir : null}
                      onSort={(dir) => setSort({ key: c.key, dir })}
                      onClearSort={() => setSort((s) => (s.key === c.key ? { key: null, dir: null } : s))}
                    />
                  </div>
                </th>
              ))}
              <th className="px-3 py-2.5 kicker text-left">Durum</th>
              <th className="px-3 py-2.5 kicker text-right">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={COLUMNS.length + 3} className="px-3 py-10 text-center text-[#8C8377]">
                  Yükleniyor...
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={COLUMNS.length + 3} className="px-3 py-10 text-center text-[#8C8377]" data-testid="empty-records">
                  {records.length === 0 ? 'Kayıt bulunamadı. "Yeni Kayıt" ile başlayın.' : "Filtreyle eşleşen kayıt yok."}
                </td>
              </tr>
            ) : (
              view.map((r, i) => (
                <tr
                  key={r.id}
                  className={`border-b border-[#E7DECC] hover:bg-[#EFE7D6] transition-colors duration-150 ${
                    i % 2 === 1 ? "bg-[#F3ECDD]/60" : ""
                  }`}
                  data-testid={`record-row-${r.id}`}
                >
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selected.has(r.id)}
                      onChange={() => toggleOne(r.id)}
                      data-testid={`select-record-${r.id}`}
                      className="w-4 h-4 accent-[#B8933F] cursor-pointer align-middle"
                    />
                  </td>
                  {COLUMNS.map((c) => (
                    <td
                      key={c.key}
                      className={`px-3 py-2 whitespace-nowrap ${
                        c.type === "money" ? `text-right tabular-nums ${cellColor(c, r[c.key])}` : ""
                      } ${c.key === "musteri" ? "font-medium" : ""}`}
                    >
                      {c.key === "musteri" ? (
                        <button
                          onClick={() => navigate(`/icra/${r.id}`)}
                          data-testid={`customer-link-${r.id}`}
                          className="text-[#B8933F] hover:underline text-left"
                        >
                          {r.musteri}
                        </button>
                      ) : c.type === "date" ? (
                        fmtDate(r[c.key])
                      ) : c.type === "money" ? (
                        fmt(r[c.key])
                      ) : (
                        r[c.key]
                      )}
                    </td>
                  ))}
                  <td className="px-3 py-2 whitespace-nowrap">
                    {r.durum === "İNFAZ" ? (
                      <span data-testid={`record-durum-${r.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-semibold bg-[#2F6E4E]/10 text-[#2F6E4E]"><Gavel className="w-3 h-3" /> İNFAZ</span>
                    ) : <span className="text-xs text-[#8C8377]">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <div className="flex justify-end gap-1">
                      {isAdmin ? (
                        <>
                          {r.from_cari && (
                            <button
                              onClick={() => undoIcra(r)}
                              aria-label="Cari'ye geri al"
                              title="Cari'ye geri al (İcraya yanlış taşındıysa)"
                              data-testid={`undo-icra-${r.id}`}
                              className="p-1.5 text-[#8C8377] hover:text-[#B8933F] hover:bg-white rounded-sm transition-colors duration-150"
                            >
                              <RotateCcw className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => toggleInfaz(r)}
                            aria-label={r.durum === "İNFAZ" ? "İnfazı geri al" : "Dosyayı infaz et"}
                            title={r.durum === "İNFAZ" ? "İnfazı geri al" : "Dosyayı infaz et"}
                            data-testid={`infaz-toggle-${r.id}`}
                            className={`p-1.5 hover:bg-white rounded-sm transition-colors duration-150 ${r.durum === "İNFAZ" ? "text-[#2F6E4E]" : "text-[#8C8377] hover:text-[#2F6E4E]"}`}
                          >
                            <Gavel className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              setEditing(r);
                              setShowForm(true);
                            }}
                            aria-label="Düzenle"
                            data-testid={`edit-record-${r.id}`}
                            className="p-1.5 text-[#8C8377] hover:text-[#B8933F] hover:bg-white rounded-sm transition-colors duration-150"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(r.id)}
                            aria-label="Sil"
                            data-testid={`delete-record-${r.id}`}
                            className="p-1.5 text-[#8C8377] hover:text-[#A63A3A] hover:bg-white rounded-sm transition-colors duration-150"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <span className="text-xs text-[#8C8377]">—</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
          {filtered.length > 0 && (
            <tfoot className="sticky bottom-0 bg-[#EFE7D6] border-t-2 border-[#B8933F] z-10">
              <tr>
                <td className="px-3 py-2"></td>
                {COLUMNS.map((c) => (
                  <td key={c.key} className={`px-3 py-2 whitespace-nowrap text-xs font-bold ${c.type === "money" ? "text-right tabular-nums" : "text-left"}`}>
                    {c.key === "musteri" ? `${filtered.length} kayıt` : c.type === "money" ? fmt(totals[c.key]) : ""}
                  </td>
                ))}
                <td></td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {showForm && (
        <RecordForm
          initial={editing}
          onClose={() => {
            setShowForm(false);
            setEditing(null);
          }}
          onSubmit={handleSubmit}
        />
      )}
      {showImport && (
        <ImportDialog
          onClose={() => setShowImport(false)}
          onDone={() => {
            setShowImport(false);
            load();
          }}
        />
      )}
      {showBulkEdit && (
        <BulkEditDialog
          count={selected.size}
          onClose={() => setShowBulkEdit(false)}
          onApply={handleBulkUpdate}
        />
      )}

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => { if (!o) setConfirm(null); }}
        title={confirm?.title}
        description={confirm?.description}
        onConfirm={confirm?.onConfirm}
      />
    </Layout>
  );
}
